import React, { FC } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { AxiosError } from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';

import { API_LINK_MANAGEMENT_URL } from '@organisms/Tables/Enterprise/LinksManagementTable/constants';
import { ITableLinksManagement } from '@organisms/Tables/Enterprise/LinksManagementTable/types';
import { errorMessage, successMessage } from '@services';
import { DEFAULT_TOAST_MESSAGE } from '../constants';
import { IDeleteLinkManagementData } from '../types';
import { Tooltip } from '@mui/material';
import { useModalState } from '@hooks';
import { ConfirmModal } from '@organisms/Modals';

interface ILinkManagementDeleteBtnprops {
  multipleValue: ITableLinksManagement[];
  refetch: () => void;
}

const LinkManagementDeleteBtn: FC<ILinkManagementDeleteBtnprops> = ({ multipleValue, refetch }) => {
  const { id: organizationId } = useParams<{ id: string }>();

  const [isOpenConfirmDeleteModal, , openConfirmDeleteModal, closeConfirmDeleteModal] =
    useModalState(false);

  const handleSuccess = () => {
    refetch();
    successMessage(DEFAULT_TOAST_MESSAGE.DELETE.SUCCESS);
  };

  const handleError = (error: AxiosError) => {
    const errorMsg = error.response?.data?.errorMessage || DEFAULT_TOAST_MESSAGE.DELETE.ERROR;
    errorMessage(errorMsg);
  };

  const { mutate: deleteLinks, isLoading: isDeleting } = useMutation<
    unknown,
    AxiosError,
    IDeleteLinkManagementData
  >(
    (data) =>
      ApiClientEnterprise.delete(`${API_LINK_MANAGEMENT_URL}${organizationId}/links`, { data }),
    {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  );

  const handleSaveLinkManagement = () => {
    if (multipleValue) {
      const listIds = multipleValue.map((value) => value.id);
      deleteLinks({ ids: listIds });
    }
  };

  return (
    <>
      <Tooltip title="Delete">
        <LoadingButton
          onClick={openConfirmDeleteModal}
          color="error"
          startIcon={<DeleteIcon fontSize="small" />}
          loading={isDeleting}
          sx={{
            padding: 0,
            minWidth: '30px',
            '& > span': {
              margin: 0,
            },
          }}
        />
      </Tooltip>
      <ConfirmModal
        open={isOpenConfirmDeleteModal}
        onCancel={closeConfirmDeleteModal}
        onSubmit={handleSaveLinkManagement}
        title={`Are you sure you want to delete link${multipleValue.length > 1 ? 's' : ''}?`}
        submitColor="error"
        submitText="Delete"
      />
    </>
  );
};

export default React.memo(LinkManagementDeleteBtn);
